"use client";

import { useState, useRef } from "react";
import { FileUploader } from "@/components/ui/FileUploader";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function ImageResizer() {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);
  const [aspectRatio, setAspectRatio] = useState<number>(0);
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [quality, setQuality] = useState(90);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "image-resizer") ?? tools[0],
    locale
  );

  const handleFilesSelected = (files: File[]) => {
    if (files.length > 0) {
      const selectedFile = files[0];
      if (!selectedFile.type.startsWith("image/")) {
        alert(ui.pleaseSelectImageFile);
        return;
      }
      setFile(selectedFile);
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);

      const img = new Image();
      img.onload = () => {
        setWidth(img.width);
        setHeight(img.height);
        setAspectRatio(img.width / img.height);
      };
      img.src = url;
    }
  };

  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newWidth = parseInt(e.target.value) || 0;
    setWidth(newWidth);
    if (maintainAspectRatio && aspectRatio) {
      setHeight(Math.round(newWidth / aspectRatio));
    }
  };

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHeight = parseInt(e.target.value) || 0;
    setHeight(newHeight);
    if (maintainAspectRatio && aspectRatio) {
      setWidth(Math.round(newHeight * aspectRatio));
    }
  };

  const handleDownload = () => {
    if (!previewUrl || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);

      const dataUrl = canvas.toDataURL(file?.type || "image/jpeg", quality / 100);
      const link = document.createElement("a");
      link.download = `resized-${file?.name}`;
      link.href = dataUrl;
      link.click();
    };
    img.src = previewUrl;
  };

  const handleReset = () => {
    setFile(null);
    setPreviewUrl(null);
    setWidth(0);
    setHeight(0);
  };

  if (!file) {
    return (
      <FileUploader
        accept="image/*"
        onFilesSelected={handleFilesSelected}
        className="min-h-[400px]"
      />
    );
  }

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6 mb-8">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:information-variant" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        <div className="space-y-4">
          <div className="relative flex min-h-[400px] items-center justify-center rounded-[20px] border-2 border-positivus-dark bg-white p-6 shadow-[0_5px_0_0_#191A23]">
            {previewUrl && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={previewUrl}
                alt={ui.preview}
                className="max-h-[350px] max-w-full rounded-[10px] object-contain"
              />
            )}
             <div className="absolute top-4 right-4 bg-black/70 text-white text-xs font-bold px-3 py-1 rounded-[10px]">
                {width} x {height} px
             </div>
          </div>
          <div className="flex justify-center">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 text-sm font-bold text-red-500 hover:text-red-700 transition-colors bg-red-50 px-4 py-2 rounded-[10px] border border-red-200 hover:border-red-400"
            >
              <Icon icon={uiIcons.refresh} className="h-4 w-4" />
              {ui.uploadDifferentImage}
            </button>
          </div>
        </div>

        <div className="space-y-6 rounded-[20px] border-2 border-positivus-dark bg-white p-8 shadow-[0_5px_0_0_#191A23]">
          <h3 className="font-bold flex items-center gap-3 text-xl text-positivus-dark border-b pb-4">
            <Icon icon={uiIcons.image} className="h-6 w-6 text-positivus-dark" />
            {ui.resizeSettings}
          </h3>
          
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-bold text-positivus-dark">{ui.widthPx}</label>
              <input
                type="number"
                value={width}
                onChange={handleWidthChange}
                className="w-full rounded-[15px] border-2 border-positivus-dark/20 bg-gray-50 px-4 py-3 text-lg font-bold text-positivus-dark focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-positivus-dark">{ui.heightPx}</label>
              <input
                type="number"
                value={height}
                onChange={handleHeightChange}
                className="w-full rounded-[15px] border-2 border-positivus-dark/20 bg-gray-50 px-4 py-3 text-lg font-bold text-positivus-dark focus:border-positivus-dark focus:outline-none focus:ring-0 transition-all"
              />
            </div>
          </div>

          <label className="flex items-center gap-4 rounded-[15px] border-2 border-positivus-dark/20 p-4 hover:border-positivus-dark transition-all cursor-pointer bg-gray-50">
             <div className={`flex items-center justify-center p-1 rounded-md border-2 ${maintainAspectRatio ? 'border-positivus-dark bg-positivus-dark text-white' : 'border-gray-300 bg-white'}`}>
                {maintainAspectRatio && <Icon icon="mdi:check" className="h-4 w-4" />}
                {!maintainAspectRatio && <div className="h-4 w-4" />}
            </div>
            <input
              type="checkbox"
              id="aspect-ratio"
              checked={maintainAspectRatio}
              onChange={(e) => setMaintainAspectRatio(e.target.checked)}
              className="hidden"
            />
            <span className="text-base font-bold text-positivus-dark">{ui.maintainAspectRatio}</span>
          </label>

          <div className="space-y-3">
             <div className="flex justify-between items-center">
                 <label className="text-sm font-bold text-positivus-dark">{ui.quality}</label>
                 <span className="px-2 py-1 rounded-[5px] bg-positivus-green/20 text-positivus-dark font-bold text-xs">{quality}%</span>
             </div>
            <input
              type="range"
              min="10"
              max="100"
              value={quality}
              onChange={(e) => setQuality(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-positivus-dark"
            />
          </div>

          <button
            onClick={handleDownload}
            className="w-full flex items-center justify-center gap-3 rounded-[20px] bg-positivus-dark px-6 py-4 text-lg font-bold text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px]"
          >
            <Icon icon={uiIcons.download} className="h-5 w-5" />
            {ui.downloadResizedImage}
          </button>
        </div>
      </div>
      
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
